import React, { useState } from 'react'
import { useSubscriptions } from './hooks/useSubscriptions'

export default function SubscriptionSettingsScreen() {
  const {
    activeModules,
    subscriptions,
    availableTrials,
    coverageMap,
    loading,
    error,
    actionLoading,
    activateTrial,
    refresh,
  } = useSubscriptions()

  const [feedback, setFeedback] = useState(null)
  const [activatingItem, setActivatingItem] = useState(null)
  const [confirmModal, setConfirmModal] = useState(null)

  const requestTrial = (item) => {
    const expiryDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
    setConfirmModal({ item, expiryDate })
  }

  const confirmTrial = async () => {
    if (!confirmModal?.item) return
    const { item } = confirmModal
    setConfirmModal(null)
    await handleStartTrial(item.item_type, item.item_code, item.name)
  }

  const handleStartTrial = async (itemType, itemCode, itemName) => {
    setFeedback(null)
    setActivatingItem(`${itemType}_${itemCode}`)
    const res = await activateTrial(itemType, itemCode)
    setActivatingItem(null)
    if (res.success) {
      setFeedback({
        type: 'success',
        message: `7-day trial activated for ${itemName}! You now have immediate access.`,
      })
    } else {
      setFeedback({
        type: 'error',
        message: res.error || 'Failed to activate trial.',
      })
    }
  }

  const activeCount = subscriptions.filter(s => s.status === 'ACTIVE' || s.status === 'TRIALING').length
  const trialingCount = subscriptions.filter(s => s.status === 'TRIALING').length

  const getStatusBadge = (status, isTrial, daysRemaining) => {
    switch (status) {
      case 'TRIALING':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            Trial · {daysRemaining !== null ? `${daysRemaining} days left` : 'Active'}
          </span>
        )
      case 'ACTIVE':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            Active {daysRemaining !== null ? `(${daysRemaining}d left)` : ''}
          </span>
        )
      case 'TRIAL_EXPIRED':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
            <i className="fa-solid fa-clock-rotate-left text-[11px]"></i>
            Trial Expired
          </span>
        )
      case 'EXPIRED':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-600 border border-gray-200">
            Expired
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {status}
          </span>
        )
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-200 pb-5">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-600 mb-1">
            <i className="fa-solid fa-shield-halved"></i> Workspace Entitlements
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            Subscriptions &amp; Validity Clocks
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage your subscription packs and individual module validities. Each product clock runs independently.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="self-start sm:self-auto inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 transition shadow-xs cursor-pointer disabled:opacity-50"
        >
          <i className={`fa-solid fa-arrows-rotate ${loading ? 'fa-spin' : ''}`}></i>
          Refresh Status
        </button>
      </div>

      {/* Global Error Banner */}
      {error && (
        <div role="alert" className="flex items-start gap-3 rounded-2xl bg-red-50 border border-red-200 p-4 text-sm text-red-800 shadow-xs">
          <i className="fa-solid fa-circle-exclamation mt-0.5 text-base text-red-500 shrink-0"></i>
          <div>
            <div className="font-bold">Subscription Sync Issue</div>
            <p className="mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Action Feedback Banner */}
      {feedback && (
        <div
          role="alert"
          className={`flex items-start justify-between gap-3 rounded-2xl p-4 text-sm shadow-xs ${
            feedback.type === 'success'
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
              : 'bg-red-50 border border-red-200 text-red-900'
          }`}
        >
          <div className="flex items-start gap-2.5">
            <i className={`fa-solid ${feedback.type === 'success' ? 'fa-circle-check text-emerald-600' : 'fa-circle-xmark text-red-600'} mt-0.5 text-base shrink-0`}></i>
            <span className="font-medium">{feedback.message}</span>
          </div>
          <button
            onClick={() => setFeedback(null)}
            className="text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
      )}

      {/* KPI Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl shrink-0">
            <i className="fa-solid fa-cubes"></i>
          </div>
          <div>
            <div className="text-2xl font-black text-gray-900">{activeModules.length}</div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Active Product Modules</div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl shrink-0">
            <i className="fa-solid fa-file-contract"></i>
          </div>
          <div>
            <div className="text-2xl font-black text-gray-900">{activeCount}</div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Active Subscriptions</div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-xl shrink-0">
            <i className="fa-solid fa-hourglass-half"></i>
          </div>
          <div>
            <div className="text-2xl font-black text-gray-900">{trialingCount}</div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Active Free Trials</div>
          </div>
        </div>
      </div>

      {/* Active & Contract Subscriptions Table */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-xs overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <i className="fa-solid fa-list-check text-blue-600"></i>
            Subscription Contracts &amp; Validities
          </h2>
          <span className="text-xs font-medium text-gray-500">
            {subscriptions.length} contract{subscriptions.length === 1 ? '' : 's'} recorded
          </span>
        </div>

        {subscriptions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <i className="fa-solid fa-box-archive text-3xl text-gray-300 mb-2"></i>
            <p className="text-sm font-medium">No subscription contracts found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-gray-50/75 border-b border-gray-100 text-xs font-bold text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3.5">Product / Pack</th>
                  <th className="px-6 py-3.5">Type</th>
                  <th className="px-6 py-3.5">Status</th>
                  <th className="px-6 py-3.5">Activated On</th>
                  <th className="px-6 py-3.5">Expires On</th>
                  <th className="px-6 py-3.5 text-right">Validity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {subscriptions.map((sub) => {
                  const coveredBy = sub.item_type === 'MODULE' ? coverageMap[sub.item_code] : null
                  return (
                    <tr key={sub.id} className="hover:bg-gray-50/50 transition">
                      <td className="px-6 py-4 font-bold text-gray-900">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2.5">
                            <i
                              className={`fa-solid ${
                                sub.item_type === 'PACK' ? 'fa-box text-blue-500' : 'fa-puzzle-piece text-emerald-500'
                              }`}
                            ></i>
                            <span>{sub.item_name}</span>
                          </div>
                          {coveredBy && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md w-fit">
                              <i className="fa-solid fa-shield-check text-[10px]"></i>
                              Covered by {coveredBy}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                          {sub.item_type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(sub.status, sub.is_trial, sub.days_remaining)}
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500">
                        {sub.starts_at ? new Date(sub.starts_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-6 py-4 text-xs font-medium text-gray-700">
                        {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : 'Never (Lifetime)'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {sub.days_remaining !== null ? (
                          <span className="font-semibold text-gray-900">
                            {sub.days_remaining} day{sub.days_remaining === 1 ? '' : 's'}
                          </span>
                        ) : (
                          <span className="text-emerald-600 font-semibold">Active</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Available 7-Day Free Trials */}
      {availableTrials.length > 0 && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <i className="fa-solid fa-gift text-amber-500"></i>
              Available 7-Day Free Trials
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              You are eligible to start an independent 7-day trial for any of the following products.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {availableTrials.map((item) => {
              const itemKey = `${item.item_type}_${item.item_code}`
              const isActivating = activatingItem === itemKey
              const coveredBy = item.item_type === 'MODULE' ? coverageMap[item.item_code] : null
              return (
                <div
                  key={itemKey}
                  className="bg-white border border-gray-200 rounded-2xl p-5 shadow-xs flex flex-col justify-between hover:border-blue-400 hover:shadow-md transition"
                >
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                        {item.item_type}
                      </span>
                      <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                        7 Days Free
                      </span>
                    </div>

                    <h3 className="text-base font-extrabold text-gray-900">{item.name}</h3>

                    {coveredBy && (
                      <div className="mt-1 text-xs text-blue-700 bg-blue-50/80 px-2.5 py-1 rounded-lg border border-blue-100 flex items-center gap-1.5 font-medium">
                        <i className="fa-solid fa-circle-info text-[11px]"></i>
                        Included in your active {coveredBy}
                      </div>
                    )}

                    {item.description && (
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{item.description}</p>
                    )}

                    {item.included_modules && item.included_modules.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                          Includes:
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {item.included_modules.map((m) => (
                            <span
                              key={m.code}
                              className="text-xs font-medium bg-gray-100 text-gray-700 px-2 py-0.5 rounded"
                            >
                              {m.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => requestTrial(item)}
                    disabled={actionLoading}
                    className="mt-5 w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold text-xs py-2.5 rounded-xl transition shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    {isActivating ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i> Activating…
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-bolt"></i> Start 7-Day Free Trial
                      </>
                    )}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl shrink-0">
                <i className="fa-solid fa-rocket"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Start Free Trial</h3>
                <p className="text-xs text-gray-500">Instant activation for your workspace</p>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-sm text-gray-700 space-y-2">
              <p>
                Start your 7-day free trial of <strong className="text-gray-900 font-semibold">{confirmModal.item.name}</strong>?
              </p>
              <div className="text-xs text-gray-500 flex items-center gap-2">
                <i className="fa-regular fa-clock text-blue-500"></i>
                <span>Expires on <strong>{confirmModal.expiryDate}</strong></span>
              </div>
              <p className="text-xs text-gray-400">
                Access unlocks immediately across your entire organization without requiring you to log out.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setConfirmModal(null)}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-100 transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmTrial}
                disabled={actionLoading}
                className="px-5 py-2 rounded-xl text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white transition shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-60"
              >
                <i className="fa-solid fa-check"></i>
                Confirm &amp; Start Trial
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Included Platform Core Modules Banner */}
      <div className="bg-gradient-to-r from-gray-50 via-slate-50 to-gray-50 border border-gray-200 rounded-2xl p-5 text-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center text-lg shrink-0 mt-0.5">
            <i className="fa-solid fa-layer-group"></i>
          </div>
          <div>
            <h3 className="font-extrabold text-gray-900">Platform Core Modules (Always Included)</h3>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              Every active tenant is automatically granted platform core modules:{' '}
              <strong className="text-gray-800">Masters, Analytics, System Audit Logs, and Standard Reports</strong>.
              These core capabilities do not require separate purchases.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
